import type { VueConstructor, WatchOptionsWithHandler } from 'vue';
import Vue from 'vue';
import { InputNumber } from '@cognitoforms/element-ui';
import { Component, Prop, Watch, Inject } from 'vue-property-decorator';

type VueComponentInternals = {
	_isMounted: boolean;
	_isDestroyed: boolean;
	_isBeingDestroyed: boolean;
	_isVue: boolean;
	_inactive: boolean;
	_directInactive: boolean;
	_hasHookEvent: boolean;
};

interface InputNumberInternals {
	// Data
	userInput: string | number;
	currentValue: string | number;
	numPrecision: number;

	// Methods
	toPrecision(num: number, precision?: number): number;
	getPrecision(value: number): number;

	_increase(val: number, step: number): number;
	_decrease(val: number, step: number): number;
	increase(): void;
	decrease(): void;

	handleBlur(event): void;
	handleFocus(event): void;

	setCurrentValue(newVal: number): void;
	handleInput(value: number | string): void;
	handleInputChange(value: number | string): void;

	readonly inputNumberDisabled: boolean;
	readonly maxDisabled: boolean;
	readonly minDisabled: boolean;
}

const InputNumberConstructor = Vue.extend(InputNumber) as VueConstructor<InputNumber & InputNumberInternals & VueComponentInternals>;

@Component({ extends: InputNumber })
export default class CInputNumber extends InputNumberConstructor {
	// -------------- Properties --------------
	@Prop({ default: 'N0' })
	format: string;

	@Prop({ default: null })
	id: string;

	// -------------- Injections --------------
	@Inject()
	readonly $format: (value: number | Date, format: string) => string;

	@Inject()
	readonly $parse: (type: NumberConstructor | DateConstructor, value: string, format?: string) => number | Date;

	// -------------- Data --------------
	isPercent: boolean;

	// -------------- LifeCycle Hooks --------------
	beforeCreate() {
		// Hooks are concated not overwritten, therefore both of the parent's value
		// watchers need to be removed
		(this.$options.watch.value as (Array<object> & WatchOptionsWithHandler<any>)).splice(0, 2);
	}

	created() {
		this.isPercent = this.format.startsWith('P');
	}

	// -------------- Computed --------------
	// [Override]
	// Mutation: Use our format parser to get the display value
	get displayValue(): string | number {
		if (this.userInput !== null)
			return this.userInput;

		return this.tryFormat(this.value, true) || this.value;
	}

	// [Override]
	// Mutation: Use rawValue since value is a string
	get minDisabled(): boolean {
		return this._decrease(this.rawValue, this.step) < this.min;
	}

	// [Override]
	// Mutation: Use rawValue since value is a string
	get maxDisabled(): boolean {
		return this._increase(this.rawValue, this.step) > this.max;
	};

	get rawValue(): number {
		if (this.value !== 0 && !this.value)
			return NaN;

		return this.tryParse(this.value);
	};

	// -------------- Watchers --------------
	// [Override]
	// Mutation: Use our parser to parse the incoming value
	// Addition: If the value is empty, set it to the minimum
	// Deletion: Do not force the value to be between the maximum and the minimum
	// Mutation: Only emit the 'input' event if the component is mounted
	@Watch('value', { immediate: true })
	onValueChanged(value: string | number, oldValue: string | number) {
		let newVal = value;

		if (value === undefined || value === '' || isNaN(value as number) || (typeof oldValue === 'string' && oldValue === ''))
			newVal = this.min || this.min;

		newVal = this.tryParse(value) || value;

		if (typeof newVal === 'number' && newVal !== undefined) {
			if (this.stepStrictly) {
				const stepPrecision = this.getPrecision(this.step);
				const precisionFactor = Math.pow(10, stepPrecision);
				newVal = Math.round(newVal / this.step) * precisionFactor * this.step / precisionFactor;
			}
			if (this.precision !== undefined) {
				newVal = this.toPrecision(newVal, this.precision);
			}
		}
		else if (typeof newVal === 'number' && newVal === undefined)
			newVal = this.min;

		this.currentValue = newVal;
		this.userInput = null;

		if (this._isMounted) {
			this.$emit('input', newVal);
		}
	}

	// -------------- Methods --------------
	// [Override]
	// Substitution: Number() for $parse() to assert string is properly converted to number given the current culture and format
	handleInputChange(value: string) {
		const newVal = this.tryParse(value) || value;

		this.setCurrentValue(newVal);

		this.userInput = null;
	}

	// [Override]
	// Deletion: Use the value directly off of the model instead of the value or zero
	increase() {
		if (this.inputNumberDisabled || this.maxDisabled)
			return;

		const newVal = this._increase(this.rawValue, this.step);

		this.setCurrentValue(newVal);
	}

	// [Override]
	// Deletion: Use the value directly off of the model instead of the value or zero
	decrease() {
		if (this.inputNumberDisabled || this.minDisabled)
			return;

		const newVal = this._decrease(this.rawValue, this.step);
		this.setCurrentValue(newVal);
	}

	// [Override]
	// Addition: Make sure that the value is correct given the format and culture
	// Mutation: The step must be adjusted to reflect the current distance between the max value and the current value if
	//			 the distance is less than the current step
	// Mutation: If the value is equal to null, return the min or zero
	_increase(val: number, step: number): number {
		if (val !== 0 && !val)
			return this.min === (Number.MIN_SAFE_INTEGER || -9007199254740990) ? 0 : this.min;

		if (val > this.max || val < this.min)
			return val;

		if (typeof val !== 'number' && val !== undefined)
			return this.currentValue as number;

		if (val < this.max && this.max - val < this.step)
			step = this.max - val;

		const precision = Math.max(this.numPrecision || 0, this.getPrecision(step), this.getPrecision(val));
		const precisionFactor = Math.pow(10, precision);
		// Solve the accuracy problem of JS decimal calculation by converting the value to integer.
		return this.toPrecision((precisionFactor * val + precisionFactor * step) / precisionFactor);
	}

	// [Override]
	// Addition: Make sure that the value is correct given the format and culture
	// Mutation: The step must be adjusted to reflect the current distance between the min value and the current value if
	//			 the distance is less than the current step
	// Mutation: If the value is equal to nul, return the min or zero
	_decrease(val: number, step: number): number {
		if (val !== 0 && !val)
			return this.min === (Number.MIN_SAFE_INTEGER || -9007199254740990) ? 0 : this.min;

		if (val > this.max || val < this.min)
			return val;

		if (typeof val !== 'number' && val !== undefined)
			return this.currentValue as number;

		if (val > this.min && val - this.min < this.step)
			step = val - this.min;

		const precision = Math.max(this.numPrecision || 0, this.getPrecision(step), this.getPrecision(val));
		const precisionFactor = Math.pow(10, precision);
		return this.toPrecision((precisionFactor * val - precisionFactor * step) / precisionFactor);
	}

	// [Override]
	// Deletion: Do not adjust the new value to be within the max and min range. Instead,
	//			 show range validation error
	setCurrentValue(newVal: number | string): void {
		if (this.userInput === null) {
			if (newVal < this.min)
				newVal = this.min;
			else if (newVal > this.max)
				newVal = this.max;
		}

		this.userInput = null;
		const val: number | string = this.tryFormat(newVal) || newVal;

		this.$emit('input', val);
		this.$emit('change', val, this.tryFormat(this.rawValue) || this.rawValue);
		this.currentValue = val;
	}

	tryFormat(val: string | number, roundValue: boolean = false): string {
		const stringVal: string | number = (typeof val === 'string' ? this.tryParse(val, roundValue) || val : val) as number;

		if (stringVal !== 0 && (!stringVal || typeof stringVal !== 'number'))
			return null;

		return this.$format(stringVal, roundValue ? this.format : this.format[0] + Math.max(this.precision, this.getPrecision(stringVal)));
	}

	tryParse(stringVal: string | number, roundValue: boolean = false): number {
		const val: string | number = (typeof stringVal === 'number' ? this.$format(stringVal, roundValue ? this.format : this.format[0] + Math.max(this.precision, this.getPrecision(stringVal))) : stringVal);

		if (!val)
			return NaN;

		return this.$parse(Number, val, this.format[0]) as number;
	}
}
